/**
 * Better Quest Designs — browser code.
 *
 * Navigation is plain links, so this file only covers the contact page:
 * the multi-select pills, client-side validation, and posting the form.
 *
 * Bundled by esbuild to `js/main.js` as a classic script — no modules at
 * runtime, so the pages still work when opened straight off disk.
 */

import {
  isValidEmail,
  type ContactPayload,
  type ContactResponse,
} from '../shared/contact.js';

/* -------------------------------------------------------------------------
   Where the contact form posts.

   Same origin as the site (the default, and what the bundled server does):
     const ENDPOINT = '/api/contact';

   Server hosted separately — use its full URL, and add this site's origin
   to ALLOWED_ORIGINS in .env so CORS lets it through:
     const ENDPOINT = 'https://api.betterquestdesigns.com/api/contact';
   ------------------------------------------------------------------------- */
const ENDPOINT = '/api/contact';

type StatusKind = 'ok' | 'error' | 'busy';

/** A required field paired with the message shown when it fails. */
interface ValidatedField {
  readonly input: HTMLInputElement | HTMLTextAreaElement;
  readonly error: HTMLElement;
  readonly check: (value: string) => boolean;
}

const nonEmpty = (value: string): boolean => value.length > 0;

function query<T extends HTMLElement>(id: string): T | null {
  return document.getElementById(id) as T | null;
}

/* -------------------------------------------------------------------------
   Phone navigation.

   Five tabs do not fit beside the logo on a phone. The stylesheet's
   fallback is a single scrolling row, which works but clips the last tab.
   This upgrades it to a Menu button and a dropdown.

   The button is built here rather than written into all six pages, so it
   only ever exists when the script that drives it is running. If this file
   fails to load, the CSS fallback is what people get — the nav is never
   hidden behind a button that cannot open.
   ------------------------------------------------------------------------- */
function initNav(): void {
  const bar = document.querySelector<HTMLElement>('.bar');
  const barIn = bar?.querySelector<HTMLElement>('.bar-in');
  const tabs = bar?.querySelector<HTMLElement>('.tabs');
  if (!bar || !barIn || !tabs) return;

  const phone = window.matchMedia('(max-width: 640px)');

  tabs.id = tabs.id || 'site-nav';

  const toggle = document.createElement('button');
  toggle.type = 'button';
  toggle.className = 'nav-toggle';
  toggle.setAttribute('aria-expanded', 'false');
  toggle.setAttribute('aria-controls', tabs.id);
  toggle.innerHTML =
    '<span class="nav-bars" aria-hidden="true"><span></span></span>' +
    '<span class="nav-label">Menu</span>';
  // Insert BEFORE the tab list, not after it. The button sits on the right
  // visually (margin-left:auto), but in the DOM it has to come first so
  // that tabbing from the button moves into the menu it just opened rather
  // than skipping past it into the page.
  barIn.insertBefore(toggle, tabs);

  bar.classList.add('has-toggle');

  const isOpen = (): boolean => toggle.getAttribute('aria-expanded') === 'true';

  function setOpen(open: boolean): void {
    toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    tabs!.classList.toggle('is-open', open);
    bar!.classList.toggle('nav-open', open);
  }

  toggle.addEventListener('click', () => setOpen(!isOpen()));

  // Following a link navigates away, but close anyway so the menu is not
  // left open if the page is restored from the back/forward cache.
  tabs.addEventListener('click', (event) => {
    if ((event.target as HTMLElement).closest('a')) setOpen(false);
  });

  document.addEventListener('keydown', (event: KeyboardEvent) => {
    if (event.key === 'Escape' && isOpen()) {
      setOpen(false);
      toggle.focus();
    }
  });

  document.addEventListener('click', (event) => {
    if (!isOpen()) return;
    const target = event.target as Node;
    if (!bar.contains(target)) setOpen(false);
  });

  // Rotating to landscape can cross the breakpoint while the menu is open;
  // leaving `is-open` set would then show the dropdown styles on a layout
  // that no longer uses them.
  const sync = (): void => {
    if (!phone.matches) setOpen(false);
  };
  phone.addEventListener('change', sync);
}

function initContactForm(): void {
  const form = query<HTMLFormElement>('c-form');
  if (!form) return; // every page loads this bundle; only one has the form

  const note = query<HTMLParagraphElement>('c-note');
  const submit = query<HTMLButtonElement>('c-send');
  const nameInput = query<HTMLInputElement>('c-name');
  const emailInput = query<HTMLInputElement>('c-email');
  const orgInput = query<HTMLInputElement>('c-org');
  const whenSelect = query<HTMLSelectElement>('c-when');
  const messageInput = query<HTMLTextAreaElement>('c-msg');
  const honeypot = query<HTMLInputElement>('c-company');

  const nameError = query<HTMLElement>('c-name-err');
  const emailError = query<HTMLElement>('c-email-err');
  const messageError = query<HTMLElement>('c-msg-err');

  // If the markup and this file ever drift apart, fail loudly in the
  // console rather than silently half-wiring the form.
  if (
    !note || !submit || !nameInput || !emailInput || !orgInput ||
    !whenSelect || !messageInput || !honeypot ||
    !nameError || !emailError || !messageError
  ) {
    console.error('[bqd] contact form is missing expected elements');
    return;
  }

  // ---- pills ------------------------------------------------------------
  const pills = Array.from(
    document.querySelectorAll<HTMLButtonElement>('.pills button')
  );

  for (const pill of pills) {
    pill.addEventListener('click', () => {
      const pressed = pill.getAttribute('aria-pressed') === 'true';
      pill.setAttribute('aria-pressed', pressed ? 'false' : 'true');
    });
  }

  const selectedNeeds = (): string[] =>
    pills
      .filter((p) => p.getAttribute('aria-pressed') === 'true')
      .map((p) => (p.textContent ?? '').trim())
      .filter(Boolean);

  const resetPills = (): void => {
    for (const pill of pills) pill.setAttribute('aria-pressed', 'false');
  };

  // ---- validation -------------------------------------------------------
  const fields: readonly ValidatedField[] = [
    { input: nameInput, error: nameError, check: nonEmpty },
    { input: emailInput, error: emailError, check: isValidEmail },
    { input: messageInput, error: messageError, check: nonEmpty },
  ];

  const isValid = (field: ValidatedField): boolean =>
    field.check(field.input.value.trim());

  function showError(field: ValidatedField, show: boolean): void {
    field.error.hidden = !show;
    field.input.setAttribute('aria-invalid', show ? 'true' : 'false');
    field.input.classList.toggle('bad', show);
  }

  // Clear a field's error as soon as it is fixed, rather than making the
  // person submit again to find out.
  for (const field of fields) {
    field.input.addEventListener('input', () => {
      if (!field.error.hidden && isValid(field)) showError(field, false);
    });
  }

  function setStatus(text: string, kind?: StatusKind): void {
    note!.textContent = text;
    note!.className = kind ? `note is-${kind}` : 'note';
  }

  // ---- submit -----------------------------------------------------------
  form.addEventListener('submit', (event: SubmitEvent) => {
    event.preventDefault();

    let firstInvalid: ValidatedField | null = null;
    for (const field of fields) {
      const ok = isValid(field);
      showError(field, !ok);
      if (!ok && !firstInvalid) firstInvalid = field;
    }

    if (firstInvalid) {
      setStatus('Please check the highlighted fields.', 'error');
      firstInvalid.input.focus();
      return;
    }

    const payload: ContactPayload = {
      name: nameInput.value.trim(),
      email: emailInput.value.trim(),
      organization: orgInput.value.trim(),
      needs: selectedNeeds(),
      timeline: whenSelect.value,
      message: messageInput.value.trim(),
      website: honeypot.value,
    };

    void send(payload);
  });

  async function send(payload: ContactPayload): Promise<void> {
    submit!.disabled = true;
    setStatus('Sending…', 'busy');

    try {
      const response = await fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const body = (await response.json().catch(() => ({}))) as Partial<ContactResponse>;

      if (response.ok) {
        form!.reset();
        resetPills();
        setStatus(
          "Thanks! Your message is on its way — we'll reply within one business day.",
          'ok'
        );
        return;
      }

      if (response.status === 429) {
        setStatus(
          'That is a few messages in a row — please try again in a little while.',
          'error'
        );
        return;
      }

      setStatus(
        ('error' in body && body.error) ||
          'Something went wrong sending that. Please email betterquestdesigns@gmail.com instead.',
        'error'
      );
    } catch {
      // Network failure, offline, DNS, CORS — nothing useful to tell them
      // beyond "it did not leave your browser", so offer the fallback.
      setStatus(
        "Couldn't reach the server. Please email betterquestdesigns@gmail.com instead.",
        'error'
      );
    } finally {
      submit!.disabled = false;
    }
  }
}

function init(): void {
  initNav();
  initContactForm();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
